import TooltipLink from "@/components/TooltipLink/TooltipLink";
import useTranslation from "@/hooks/useTranslation";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { message, Popconfirm, Tooltip } from "antd";
import { ReactNode } from "react";
import { Material } from "./MaterialOverview/MaterialsOverview";
import { useDeleteMaterials } from "./useMaterials";

export default function DeleteMaterialAction({
  children,
  materials,
  onSucces,
  deselectUndeletableProducts,
}: {
  children: ReactNode;
  materials: Material[];
  onSucces?: () => void;
  deselectUndeletableProducts?: () => void;
}) {
  const deleteMaterials = useDeleteMaterials();

  const materialText =
    materials.length === 1
      ? "material"
      : `${materials.length} material${materials.length > 1 && "s"}`;

  const disabled = materials.some((x) => x.relations > 0);
  const undeletableCount = materials.filter((x) => x.relations > 0).length;
  const translation = useTranslation();

  return (
    <Popconfirm
      title={`Delete ${materialText}`}
      description={
        <>
          <p>Deleting a material also deletes:</p>
          <ul>
            <li>matches to the impact reference</li>
            <li>related unit conversions</li>
          </ul>
          <p style={{ color: "#F5222D" }}>This action can not be undone.</p>
        </>
      }
      onConfirm={() =>
        deleteMaterials
          .mutateAsync({ materialIds: materials.map((x) => x.id) })
          .then((result) => {
            if (result.status === 200) {
              message.success(`Material${materials.length > 1 ? "s" : ""} removed.`);
              onSucces && onSucces();
            }
          })
          .catch(() => message.error(translation.errors.general))
      }
      onCancel={() => {}}
      icon={<ExclamationCircleFilled style={{ color: "#F5222D" }} />}
      okText={`Delete ${materialText}`}
      cancelText="Cancel"
      placement="bottom"
      okType="danger"
      okButtonProps={{ loading: deleteMaterials.isLoading }}
      disabled={disabled}
    >
      <Tooltip
        placement="bottom"
        title={
          disabled &&
          (materials.length === 1 ? (
            <p>
              This material can not be deleted as it is referenced in a bill of material. Delete
              these bill of materials first to remove this material.
            </p>
          ) : (
            <p>
              {undeletableCount} of the material you selected are referenced in a bill of material.{" "}
              {deselectUndeletableProducts ? (
                <>
                  <TooltipLink onClick={() => deselectUndeletableProducts()}>
                    Click here
                  </TooltipLink>{" "}
                  to deselect them or remove the related bill of materials to continue.
                </>
              ) : (
                <>Delete the related bill of materials to continue.</>
              )}
            </p>
          ))
        }
      >
        {children}
      </Tooltip>
    </Popconfirm>
  );
}
